import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, LinearProgress, TablePagination,
    MenuItem, Select, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, Divider, CircularProgress, useMediaQuery,
    IconButton, Tooltip, FormControlLabel, Switch, FormGroup, Checkbox
} from '@mui/material';
import { CheckCircle, Cancel, Edit as EditIcon } from '@mui/icons-material';
import { FaPlusCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import PageLoader from '../../../Reusable/PageLoader';
import baseURL from '../config';
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import { formatNumberWithCommas } from '../../../Reusable/numberUtils';

const MS_COLORS = {
    primary: '#2D4A3D',
    primaryLight: '#EBF5EE',
    text: '#323130',
    textSecondary: '#605D5C',
    background: '#FFFFFF',
    backgroundLight: '#F3F2F1',
    border: '#E1E1E1',
    hoverBackground: '#F0F0F0',
    white: '#FFFFFF',
    accent: '#107C10',
    red: '#D83B01',
};

const YambaCares = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchCampaigns = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${baseURL}/admin/campaigns`, { credentials: 'include' });
            if (!res.ok) throw new Error('Failed to fetch campaigns.');
            const data = await res.json();
            setCampaigns(data.filter(c => c.category === 'yamba_cares'));
        } catch (err) {
            console.error('Error fetching Yamba Cares campaigns:', err);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

    if (loading) {
        return (
            <DashboardLayout>
                <PageLoader message="Loading Yamba Cares campaigns ..." />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <YambaCaresManagement campaigns={campaigns} onUpdate={fetchCampaigns} />
        </DashboardLayout>
    );
};

// ─── Inner management component ──────────────────────────────────────────────

const YambaCaresManagement = ({ campaigns, onUpdate }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [statusFilter, setStatusFilter] = useState('all');
    const isSmallScreen = useMediaQuery('(max-width:600px)');

    const FREQUENCIES = ['daily', 'weekly', 'monthly', 'annually'];

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', goal: '', goalRaw: '', description: '', accepts_recurring: false, recurring_frequencies: [] });
    const [createImage, setCreateImage] = useState(null);
    const [isCreating, setIsCreating] = useState(false);

    // Edit dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({ id: null, name: '', goal: '', goalRaw: '', description: '', accepts_recurring: false, recurring_frequencies: [] });
    const [editImage, setEditImage] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    // Close dialog
    const [closeOpen, setCloseOpen] = useState(false);
    const [closeCampaignId, setCloseCampaignId] = useState(null);
    const [closeReason, setCloseReason] = useState('');
    const [isClosing, setIsClosing] = useState(false);

    const formatAmount = (val) => 'UGX ' + Math.floor(val).toLocaleString();

    const handleGoalChange = (raw, setter) => {
        const cleaned = raw.replace(/,/g, '').replace(/\D/g, '');
        setter(prev => ({ ...prev, goalRaw: cleaned, goal: formatNumberWithCommas(cleaned) }));
    };

    // Filter
    const filtered = campaigns.filter(c => {
        if (statusFilter === 'open') return c.status === 'open';
        if (statusFilter === 'closed') return c.status === 'closed';
        return true;
    });

    // ─── Create ──────────────────────────────────────────────────────────────

    const handleCreate = async () => {
        if (!createForm.name.trim() || !createForm.goalRaw.trim() || !createForm.description.trim()) {
            toast.error('Please fill in all required fields.');
            return;
        }
        if (!createImage) {
            toast.error('Please upload a campaign image.');
            return;
        }
        if (createForm.accepts_recurring && createForm.recurring_frequencies.length === 0) {
            toast.error('Please select at least one recurring frequency.');
            return;
        }

        setIsCreating(true);
        const formData = new FormData();
        formData.append('name', createForm.name);
        formData.append('goal', createForm.goalRaw);
        formData.append('description', createForm.description);
        formData.append('image', createImage);
        formData.append('accepts_recurring', createForm.accepts_recurring);
        formData.append('recurring_frequencies', JSON.stringify(createForm.recurring_frequencies));

        try {
            const res = await fetch(`${baseURL}/admin/yamba-cares/campaigns`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to create campaign.');

            toast.success('Yamba Cares campaign created!');
            setCreateOpen(false);
            setCreateForm({ name: '', goal: '', goalRaw: '', description: '', accepts_recurring: false, recurring_frequencies: [] });
            setCreateImage(null);
            onUpdate();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setIsCreating(false);
        }
    };

    // ─── Edit ────────────────────────────────────────────────────────────────

    const openEditDialog = (campaign) => {
        let freqs = [];
        if (campaign.recurring_frequencies) {
            try {
                freqs = typeof campaign.recurring_frequencies === 'string'
                    ? JSON.parse(campaign.recurring_frequencies)
                    : campaign.recurring_frequencies;
            } catch (e) { freqs = []; }
        }
        setEditForm({
            id: campaign.id,
            name: campaign.name,
            goalRaw: String(Math.floor(campaign.goal)),
            goal: formatNumberWithCommas(String(Math.floor(campaign.goal))),
            description: campaign.description || '',
            accepts_recurring: !!campaign.accepts_recurring,
            recurring_frequencies: Array.isArray(freqs) ? freqs : [],
        });
        setEditImage(null);
        setEditOpen(true);
    };

    const handleEdit = async () => {
        if (!editForm.name.trim() || !editForm.goalRaw.trim()) {
            toast.error('Name and goal are required.');
            return;
        }
        if (editForm.accepts_recurring && editForm.recurring_frequencies.length === 0) {
            toast.error('Please select at least one recurring frequency.');
            return;
        }

        setIsEditing(true);
        const formData = new FormData();
        formData.append('name', editForm.name);
        formData.append('goal', editForm.goalRaw);
        if (editForm.description) formData.append('description', editForm.description);
        if (editImage) formData.append('image', editImage);
        formData.append('accepts_recurring', editForm.accepts_recurring);
        formData.append('recurring_frequencies', JSON.stringify(editForm.recurring_frequencies));

        try {
            const res = await fetch(`${baseURL}/admin/yamba-cares/campaigns/${editForm.id}`, {
                method: 'PUT',
                body: formData,
                credentials: 'include',
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to update campaign.');

            toast.success('Campaign updated!');
            setEditOpen(false);
            onUpdate();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setIsEditing(false);
        }
    };

    // ─── Close ───────────────────────────────────────────────────────────────

    const handleClose = async () => {
        if (!closeReason.trim()) {
            toast.error('Please provide a reason.');
            return;
        }

        setIsClosing(true);
        try {
            const res = await fetch(`${baseURL}/admin/campaigns/${closeCampaignId}/close`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason: closeReason }),
                credentials: 'include',
            });
            if (res.ok) {
                toast.success('Campaign closed.');
                setCloseOpen(false);
                setCloseReason('');
                onUpdate();
            } else {
                const err = await res.json();
                toast.error(err.message || 'Failed to close campaign.');
            }
        } catch (err) {
            toast.error('Error closing campaign.');
        } finally {
            setIsClosing(false);
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color={MS_COLORS.text}>
                        Yamba Cares Fund
                    </Typography>
                    <Typography variant="body2" color={MS_COLORS.textSecondary} mt={0.5}>
                        Create and manage campaigns run by the Yamba Africa team.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<FaPlusCircle />}
                    onClick={() => setCreateOpen(true)}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: MS_COLORS.primary,
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        px: 2, py: 1,
                        boxShadow: 0,
                        '&:hover': { boxShadow: 0 },
                    }}
                >
                    Create Campaign
                </Button>
            </Box>

            {/* Filter */}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="body2" color={MS_COLORS.textSecondary}>Filter:</Typography>
                <Select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                    size="small"
                    sx={{ minWidth: 160, fontSize: '0.875rem' }}
                >
                    <MenuItem value="all">All Campaigns</MenuItem>
                    <MenuItem value="open">Open Campaigns</MenuItem>
                    <MenuItem value="closed">Closed Campaigns</MenuItem>
                </Select>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${MS_COLORS.border}` }}>
                <Table size={isSmallScreen ? 'small' : 'medium'}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: MS_COLORS.backgroundLight }}>
                            <TableCell sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>Campaign Name</TableCell>
                            {!isSmallScreen && <TableCell sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>Raised (UGX)</TableCell>}
                            <TableCell sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>Goal (UGX)</TableCell>
                            {!isSmallScreen && <TableCell sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>Progress</TableCell>}
                            <TableCell sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: MS_COLORS.textSecondary }}>
                                    No Yamba Cares campaigns found. Create one to get started.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((c) => {
                                const pct = c.goal > 0 ? Math.min((c.raised / c.goal) * 100, 100) : 0;
                                return (
                                    <TableRow key={c.id} hover>
                                        <TableCell sx={{ color: MS_COLORS.text, fontWeight: 500 }}>{c.name}</TableCell>
                                        {!isSmallScreen && <TableCell>{formatAmount(c.raised)}</TableCell>}
                                        <TableCell>{formatAmount(c.goal)}</TableCell>
                                        {!isSmallScreen && (
                                            <TableCell sx={{ minWidth: 150 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={pct}
                                                    sx={{
                                                        height: 8, borderRadius: 5,
                                                        backgroundColor: '#eee',
                                                        '& .MuiLinearProgress-bar': { backgroundColor: c.status === 'open' ? MS_COLORS.accent : '#999' }
                                                    }}
                                                />
                                                <Typography variant="caption" color={MS_COLORS.textSecondary}>
                                                    {pct.toFixed(0)}%
                                                </Typography>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            {c.status === 'open' ? (
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <CheckCircle sx={{ color: MS_COLORS.accent, fontSize: 18 }} />
                                                    <Typography variant="body2" color={MS_COLORS.accent}>Open</Typography>
                                                </Box>
                                            ) : (
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Cancel sx={{ color: MS_COLORS.red, fontSize: 18 }} />
                                                    <Typography variant="body2" color={MS_COLORS.red}>Closed</Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={1}>
                                                {c.status === 'open' && (
                                                    <>
                                                        <Tooltip title="Edit campaign">
                                                            <IconButton size="small" onClick={() => openEditDialog(c)} sx={{ color: MS_COLORS.primary }}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                                            onClick={() => { setCloseCampaignId(c.id); setCloseOpen(true); }}
                                                        >
                                                            Close
                                                        </Button>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(e, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
            />

            {/* ─── Create Campaign Dialog ─────────────────────────────────── */}
            <Dialog open={createOpen} onClose={() => !isCreating && setCreateOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                    Create Yamba Cares Campaign
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ py: 2 }}>
                    <TextField
                        fullWidth label="Campaign Name" variant="outlined"
                        value={createForm.name}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        required disabled={isCreating}
                    />
                    <TextField
                        fullWidth label="Goal Amount (UGX)" type="text" variant="outlined"
                        value={createForm.goal}
                        onChange={(e) => handleGoalChange(e.target.value, setCreateForm)}
                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        required disabled={isCreating}
                    />
                    <Typography variant="body2" color={MS_COLORS.textSecondary} mb={1}>Campaign Description</Typography>
                    <div data-color-mode="light" style={{ marginBottom: '16px' }}>
                        <MDEditor value={createForm.description} onChange={(v) => setCreateForm(prev => ({ ...prev, description: v || '' }))} height={200} />
                    </div>
                    <Typography variant="body2" color={MS_COLORS.textSecondary} mb={1}>Campaign Image</Typography>
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/jpg"
                        onChange={(e) => setCreateImage(e.target.files[0] || null)}
                        disabled={isCreating}
                    />

                    {/* Recurring donations */}
                    <Box sx={{ mt: 3 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={createForm.accepts_recurring}
                                    onChange={(e) => setCreateForm(prev => ({
                                        ...prev,
                                        accepts_recurring: e.target.checked,
                                        recurring_frequencies: e.target.checked ? prev.recurring_frequencies : [],
                                    }))}
                                    color="primary"
                                />
                            }
                            label={<Typography variant="body2" fontWeight={600}>Accept recurring donations</Typography>}
                        />
                        {createForm.accepts_recurring && (
                            <Box sx={{ mt: 1, pl: 1 }}>
                                <Typography variant="caption" sx={{ color: MS_COLORS.textSecondary, display: 'block', mb: 0.5 }}>
                                    Select available frequencies (at least one required):
                                </Typography>
                                <FormGroup row>
                                    {FREQUENCIES.map((freq) => (
                                        <FormControlLabel
                                            key={freq}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={createForm.recurring_frequencies.includes(freq)}
                                                    onChange={(e) => {
                                                        const updated = e.target.checked
                                                            ? [...createForm.recurring_frequencies, freq]
                                                            : createForm.recurring_frequencies.filter(f => f !== freq);
                                                        setCreateForm(prev => ({ ...prev, recurring_frequencies: updated }));
                                                    }}
                                                    sx={{ color: MS_COLORS.primary, '&.Mui-checked': { color: MS_COLORS.primary } }}
                                                />
                                            }
                                            label={<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{freq}</Typography>}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none', color: MS_COLORS.red }} disabled={isCreating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreate} variant="contained"
                        sx={{ textTransform: 'none', backgroundColor: MS_COLORS.primary }}
                        disabled={isCreating || !createImage}
                        startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isCreating ? 'Creating...' : 'Create Campaign'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── Edit Campaign Dialog ───────────────────────────────────── */}
            <Dialog open={editOpen} onClose={() => !isEditing && setEditOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                    Edit Yamba Cares Campaign
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ py: 2 }}>
                    <TextField
                        fullWidth label="Campaign Name" variant="outlined"
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        required disabled={isEditing}
                    />
                    <TextField
                        fullWidth label="Goal Amount (UGX)" type="text" variant="outlined"
                        value={editForm.goal}
                        onChange={(e) => handleGoalChange(e.target.value, setEditForm)}
                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        required disabled={isEditing}
                    />
                    <Typography variant="body2" color={MS_COLORS.textSecondary} mb={1}>Campaign Description</Typography>
                    <div data-color-mode="light" style={{ marginBottom: '16px' }}>
                        <MDEditor value={editForm.description} onChange={(v) => setEditForm(prev => ({ ...prev, description: v || '' }))} height={200} />
                    </div>
                    <Typography variant="body2" color={MS_COLORS.textSecondary} mb={1}>Replace Image (optional)</Typography>
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/jpg"
                        onChange={(e) => setEditImage(e.target.files[0] || null)}
                        disabled={isEditing}
                    />

                    {/* Recurring donations */}
                    <Box sx={{ mt: 3 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editForm.accepts_recurring}
                                    onChange={(e) => setEditForm(prev => ({
                                        ...prev,
                                        accepts_recurring: e.target.checked,
                                        recurring_frequencies: e.target.checked ? prev.recurring_frequencies : [],
                                    }))}
                                    color="primary"
                                />
                            }
                            label={<Typography variant="body2" fontWeight={600}>Accept recurring donations</Typography>}
                        />
                        {editForm.accepts_recurring && (
                            <Box sx={{ mt: 1, pl: 1 }}>
                                <Typography variant="caption" sx={{ color: MS_COLORS.textSecondary, display: 'block', mb: 0.5 }}>
                                    Select available frequencies (at least one required):
                                </Typography>
                                <FormGroup row>
                                    {FREQUENCIES.map((freq) => (
                                        <FormControlLabel
                                            key={freq}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={editForm.recurring_frequencies.includes(freq)}
                                                    onChange={(e) => {
                                                        const updated = e.target.checked
                                                            ? [...editForm.recurring_frequencies, freq]
                                                            : editForm.recurring_frequencies.filter(f => f !== freq);
                                                        setEditForm(prev => ({ ...prev, recurring_frequencies: updated }));
                                                    }}
                                                    sx={{ color: MS_COLORS.primary, '&.Mui-checked': { color: MS_COLORS.primary } }}
                                                />
                                            }
                                            label={<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{freq}</Typography>}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none', color: MS_COLORS.red }} disabled={isEditing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEdit} variant="contained"
                        sx={{ textTransform: 'none', backgroundColor: MS_COLORS.primary }}
                        disabled={isEditing}
                        startIcon={isEditing ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isEditing ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── Close Campaign Dialog ──────────────────────────────────── */}
            <Dialog open={closeOpen} onClose={() => !isClosing && setCloseOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 'bold' }}>Close Campaign</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color={MS_COLORS.textSecondary} mb={2}>
                        Please provide a reason for closing this campaign.
                    </Typography>
                    <TextField
                        fullWidth multiline rows={3} variant="outlined"
                        label="Reason for closure"
                        value={closeReason}
                        onChange={(e) => setCloseReason(e.target.value)}
                        disabled={isClosing}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setCloseOpen(false); setCloseReason(''); }} sx={{ textTransform: 'none' }} disabled={isClosing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleClose} variant="contained" color="error"
                        sx={{ textTransform: 'none' }}
                        disabled={isClosing || !closeReason.trim()}
                        startIcon={isClosing ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isClosing ? 'Closing...' : 'Close Campaign'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default YambaCares;
